import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { getCountryCallingCode } from 'libphonenumber-js';
import get from 'lodash/get';
import has from 'lodash/has';
import { localiseUrl } from 'src/common/utils/format-localised-url';
import Button from 'wonderbly-components/lib/Button';
import InlineLabel from 'wonderbly-components/lib/InlineLabel';
import TextField from 'wonderbly-components/lib/TextField';
import RichText from '../../Prismic/RichText';
import Select from 'wonderbly-components/lib/Select';
import FieldDescription from './FieldDescription';
import CountryFlag from '../CountryFlag';
import FieldError from './FieldError';
import ShippingMethodsField from './ShippingMethodsField';
import CountryDropdown from '../CountryDropdown';
import cx from 'classnames';
import RadioInput from 'wonderbly-components/lib/RadioInput';

const Field = props => {
  const { field, index, errors } = props;
  const name = get(field, 'primary.name');

  if (has(field, 'primary.name') && !name) {
    // Some fields, such as submit buttons, descriptions, and line breaks, dont have "name" as an option for
    // the content managers to fill in. This is because these field dont need the DOM property `name`. So because
    // of this, we check; if `name` is a property on the object `field.primary` first before we decided if the
    // field needs to be hidden.
    return null;
  }

  let width = get(field, 'primary.width') || '100%';
  if (width !== '100%') {
    width = props.media.tabletUp ? `calc(${width} - 5px)` : '100%';
  }

  const fieldId = `${field.type}--${name}`;

  switch (field.type) {
    case 'text_field':
      return (
        <div
          className={`field-container ${field.type} ${fieldId}`} key={`${field.type}-${index}`}
          style={{ width }}>
          <InlineLabel htmlFor={fieldId}>{field.primary.label}</InlineLabel>
          <TextField
            type="text"
            name={name}
            id={fieldId}
            value={props.values && props.values[name]}
            onChange={props.handleChange}
            onClick={props.handleClick}
          />
          {errors &&
            <FieldError
              message={errors[name]}
              visible={errors[name]}
            />
          }
          <FieldDescription
            content={field.primary.description}
          />
        </div>
      );
    case 'email_field':
      return (
        <div
          className={`field-container ${field.type} ${fieldId}`} key={`${field.type}-${index}`}
          style={{ width }}>
          <InlineLabel htmlFor={fieldId}>{field.primary.label}</InlineLabel>
          <TextField
            type="email"
            name={name}
            id={fieldId}
            value={props.values && props.values[name]}
            onChange={props.handleChange}
            onClick={props.handleClick}
          />
          {errors &&
            <FieldError
              message={errors[name]}
              visible={errors[name]}
            />
          }
          <FieldDescription
            content={field.primary.description}
          />
        </div>
      );
    case 'phone_field':
      return (
        <div
          className={`field-container ${field.type} ${fieldId}`} key={`${field.type}-${index}`}
          style={{ width }}>
          <InlineLabel htmlFor={fieldId}>{field.primary.label}</InlineLabel>
          <TextField
            type="tel"
            name={name}
            id={fieldId}
            value={props.values && props.values[name]}
            onChange={props.handleChange}
            onClick={props.handleClick}
          />
          {errors &&
            <FieldError
              message={errors[name]}
              visible={errors[name]}
            />
          }
          <FieldDescription
            content={field.primary.description}
          />
        </div>
      );
    case 'select_input':
      return (
        <div
          className={`field-container ${field.type} ${fieldId}`} key={`${field.type}-${index}`}
          style={{ width }}>
          {field.items.length > 0 &&
            <Fragment>
              <InlineLabel htmlFor={fieldId}>{field.primary.label}</InlineLabel>
              <Select
                value={props.values && props.values[name]}
                onChange={props.handleChange}
                options={field.items}
                name={name}
                id={fieldId}
                onClick={props.handleClick}
              />
              {errors &&
                <FieldError
                  message={errors[name]}
                  visible={errors[name]}
                />
              }
              <FieldDescription
                content={field.primary.description}
              />
            </Fragment>
          }
        </div>
      );
    case 'checkbox_input':
      return (
        <div className={`field-container ${field.type} ${fieldId}`} key={`${field.type}-${index}`}>
          <TextField
            type="checkbox"
            name={name}
            id={fieldId}
            checked={props.values && !!props.values[name]}
            onChange={props.handleChange}
            onClick={props.handleClick}
          />
          <InlineLabel htmlFor={fieldId}>
            <RichText fields={[{ text: field.primary.label }]} />
          </InlineLabel>
          {errors &&
            <FieldError
              message={errors[name]}
              visible={errors[name]}
            />
          }
          <FieldDescription
            content={field.primary.description}
          />
        </div>
      );
    case 'radio_input':
      return (
        <div className={`field-container ${field.type} ${fieldId}`} key={`${field.type}-${index}`}>
          {field.items.length > 0 &&
            <Fragment>
              {field.items.map(option => {
                const optionClassName = `${field.type}__option`;
                const isChecked = parseInt(props.values[name], 10) === option.value;
                let label = (
                  <div className={`${optionClassName}__content`}>
                    <div className={`${optionClassName}__label`}>
                      <RichText fields={[{ text: option.label }]} />
                    </div>
                    <div className={`${optionClassName}__description`}>
                      <RichText fields={option.description} />
                    </div>
                  </div>
                );

                if (props.customLabel) {
                  label = props.customLabel(option);
                }

                return (
                  <div
                    className={cx(optionClassName, {
                      [`${optionClassName}--is-checked`]: isChecked,
                    })}
                    key={option.value}>
                    <RadioInput
                      id={`${fieldId}--${option.value}`}
                      value={option.id}
                      name={name}
                      data-option={option.value}
                      checked={isChecked}
                      onChange={props.handleChange}
                      className={`${field.type}__radio-input`}
                      label={label}
                    />
                  </div>
                );
              })}
              {errors &&
                <FieldError
                  message={errors[name]}
                  visible={errors[name]}
                />
              }
            </Fragment>
          }
        </div>
      );
    case 'country_dropdown':
      return (
        <CountryDropdown
          key={fieldId}
          countries={field.primary.countries}
          onCountryChange={props.onCountryChange}
          width={width}
          name={name}
          value={props.values && props.values[name]}
          label={field.primary.label}
          description={field.primary.description}
          error={errors && errors[name]}
        />
      );
    case 'description':
      // TODO: Remove handling old description
      const description = get(field, 'primary.content') || field.description;
      return (
        <RichText fields={description} key={`${field.type}-${index}`} />
      );
    case 'line_break':
      return (
        <hr className={`field-container ${field.type}`} key={`${field.type}-${index}`} />
      );
    case 'submit':
      return (
        <Button
          colour="green"
          type="submit"
          fullWidth
          disabled={props.isSubmitting}
          key={`${field.type}-${index}`}>
          {field.primary.label}
        </Button>
      );

    // TODO: Old fields, remove all this when all forms are migrated to new user form document.
    case 'first_name':
      return (
        <div className={`field-container ${field.type}`} key={`${field.type}-${index}`}>
          <InlineLabel htmlFor={field.type}>{field.label}</InlineLabel>
          <TextField
            type="text"
            name="firstname"
            id={field.type}
            value={props.values && props.values.firstname}
            onChange={props.handleChange}
            onClick={props.handleClick}
          />
          <FieldDescription
            content={field.description}
          />
          {errors &&
            <FieldError errors={errors} field={field} message={errors.firstname} visible={errors.firstname} />
          }
        </div>
      );
    case 'last_name':
      return (
        <div className={`field-container ${field.type}`} key={`${field.type}-${index}`}>
          <InlineLabel htmlFor={field.type}>{field.label}</InlineLabel>
          <TextField
            type="text"
            name="lastname"
            id={field.type}
            value={props.values && props.values.lastname}
            onChange={props.handleChange}
            onClick={props.handleClick}
          />
          <FieldDescription
            content={field.description}
          />
          {errors &&
            <FieldError errors={errors} field={field} message={errors.lastname} visible={errors.lastname} />
          }
        </div>
      );
    case 'password_confirmation':
      return (
        <div className={`field-container ${field.type}`} key={`${field.type}-${index}`}>
          <InlineLabel htmlFor={field.type}>{field.label}</InlineLabel>
          <TextField
            type="password"
            name={field.type}
            id={field.type}
            value={props.values && props.values[field.type]}
            onChange={props.handleChange}
            onClick={props.handleClick}
          />
          <FieldDescription
            content={field.description}
          />
          {errors &&
            <FieldError errors={errors} field={field} message={errors[field.type]} visible={errors[field.type]} />
          }
        </div>
      );
    case 'marketing_opt_in':
      return (
        <div className={`field-container ${field.type}`} key={`${field.type}-${index}`}>
          <TextField
            type="checkbox"
            name={field.type}
            id={field.type}
            checked={props.values && !!props.values[field.type]}
            onChange={props.handleChange}
            onClick={props.handleClick}
          />
          <InlineLabel htmlFor={field.type}>{field.label}</InlineLabel>
          <FieldDescription
            content={field.description}
          />
          {errors &&
            <FieldError errors={errors} field={field} message={errors[field.type]} visible={errors[field.type]} />
          }
        </div>
      );
    case 'log_in':
    case 'create_account':
    case 'verify_email':
    case 'resend_email':
    case 'update_account':
    case 'continue_to_delivery':
    case 'continue_to_payment':
      return (
        <Button
          colour="green"
          type="submit"
          fullWidth
          disabled={props.isSubmitting}
          key={`${field.type}-${index}`}>
          {field.label}
        </Button>
      );
    case 'new_user':
      return (
        <Button
          href={localiseUrl(props.country, '/signup')}
          fullWidth
          key={`${field.type}-${index}`}>
          {field.label}
        </Button>
      );
    case 'back_to_login':
      return (
        <a href={localiseUrl(props.country, '/login')} key={`${field.type}-${index}`} >
          {field.label}
        </a>
      );
    case 'forgot_password':
      return (
        <a href={localiseUrl(props.country, '/forgot-password')} key={`${field.type}-${index}`} >
          {field.label}
        </a>
      );
    case 'phone_number':
      return (
        <div className={`field-container ${field.type}`} key={`${field.type}-${index}`}>
          <InlineLabel htmlFor={field.type}>{field.label}</InlineLabel>
          <div className="phone-number-selector-wrapper">
            <div className="flag-wrapper">
              <CountryFlag selectedCountry={props.selectedCountry} flagCountry={props.flagCountry} />
              <Select
                onChange={props.onFlagCountryChange}
                value={`${props.selectedCountry}`}
                options={get(props, 'countries', []).map(({ label, value }) => ({
                  value,
                  label: `${label} (+${value === 'PN' ? '64' : getCountryCallingCode(value || '')})`
                }))} // Pitcairn Islands's (PN) code is not supported by the libphonenumber library, so I'm manually handling it here
                className="shipping-country-list"
                id={field.type}
                name="phone"
              />
            </div>
            <TextField
              type="tel"
              name={field.type}
              id={field.type}
              value={props.values && props.values[field.type]}
              onChange={props.onPhoneNumberChange}
              onBlur={props.handleBlur}
              onClick={props.handleClick}
            />
          </div>
          <FieldDescription
            content={field.description}
          />
          {errors &&
            <FieldError errors={errors} field={field} message={errors[field.type]} visible={errors[field.type]} />
          }
        </div>
      );
    case 'address_1':
      return (
        <div className={`field-container ${field.type}`} key={`${field.type}-${index}`}>
          <InlineLabel htmlFor={field.type}>{field.label}</InlineLabel>
          <TextField
            type="text"
            name="address1"
            id={field.type}
            value={props.values && props.values.address1}
            onChange={props.handleChange}
            onClick={props.handleClick}
          />
          <FieldDescription
            content={field.description}
          />
          {errors &&
            <FieldError errors={errors} field={field} message={errors.address1} visible={errors.address1} />
          }
        </div>
      );
    case 'address_2':
      return (
        <div className={`field-container ${field.type}`} key={`${field.type}-${index}`}>
          <InlineLabel htmlFor={field.type}>{field.label}</InlineLabel>
          <TextField
            type="text"
            name="address2"
            id={field.type}
            value={props.values && props.values.address2}
            onChange={props.handleChange}
            onClick={props.handleClick}
          />
          <FieldDescription
            content={field.description}
          />
          {errors &&
            <FieldError errors={errors} field={field} message={errors.address2} visible={errors.address2} />
          }
        </div>
      );
    case 'postcode':
      return (
        <div className={`field-container ${field.type}`} key={`${field.type}-${index}`}>
          <InlineLabel htmlFor={field.type}>{field.label}</InlineLabel>
          <TextField
            type="text"
            name="zipcode"
            id={field.type}
            value={props.values && props.values.zipcode}
            onChange={props.handleChange}
            onClick={props.handleClick}
          />
          <FieldDescription
            content={field.description}
          />
          {errors &&
            <FieldError errors={errors} field={field} message={errors.zipcode} visible={errors.zipcode} />
          }
        </div>
      );
    case 'country':
      return (
        <div className={`field-container ${field.type}`} key={`${field.type}-${index}`}>
          <InlineLabel htmlFor={field.type}>{field.label}</InlineLabel>
          <Select
            onChange={props.onCountryChange}
            value={props.values && props.values[field.type]}
            options={props.countries}
            name={field.type}
            className="shipping-country-list"
            id="shipping-countries"
          />
          <FieldDescription
            content={field.description}
          />
          {errors &&
            <FieldError errors={errors} field={field} message={errors[field.type]} visible={errors[field.type]} />
          }
        </div>
      );
    case 'state':
      return (
        <div className={`field-container ${field.type}`} key={`${field.type}-${index}`}>
          <InlineLabel htmlFor={field.type}>{field.label}</InlineLabel>
          {props.currentCountryStates.length > 0 &&
            <Select
              value={props.values && props.values[field.type]}
              onChange={props.handleChange}
              options={props.currentCountryStates}
              name={field.type}
              className="shipping-state-list"
              id="shipping-states"
              onClick={props.handleClick}
            />
          }
          <FieldDescription
            content={field.description}
          />
          {errors &&
            <FieldError errors={errors} field={field} message={errors[field.type]} visible={errors[field.type]} />
          }
        </div>
      );
    case 'shipping_methods':
      return (
        <ShippingMethodsField {...props} />
      );
    default:
      return (
        <div className={`field-container ${field.type}`} key={`${field.type}-${index}`}>
          <InlineLabel htmlFor={field.type}>{field.label}</InlineLabel>
          <TextField
            type={field.type}
            name={field.type}
            id={field.type}
            value={props.values && props.values[field.type]}
            onChange={props.handleChange}
            onClick={props.handleClick}
          />
          <FieldDescription
            content={field.description}
          />
          {errors &&
            <FieldError errors={errors} field={field} message={errors[field.type]} visible={errors[field.type]} />
          }
        </div>
      );
  }
};

Field.propTypes = {
  formType: PropTypes.string.isRequired,
  field: PropTypes.object,
  currentCountryStates: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    })
  ),
  countries: PropTypes.array,
  handleChange: PropTypes.func.isRequired,
  onCountryChange: PropTypes.func,
  isSubmitting: PropTypes.bool.isRequired,
  onPhoneNumberChange: PropTypes.func,
  handleBlur: PropTypes.func.isRequired,
  onFlagCountryChange: PropTypes.func,
  flagCountry: PropTypes.object,
  selectedCountry: PropTypes.string,
  values: PropTypes.shape({
    firstname: PropTypes.string,
    lastname: PropTypes.string,
    address1: PropTypes.string,
    address2: PropTypes.string,
    zipcode: PropTypes.string,
    selectedShippingRateId: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
  }).isRequired,
  country: PropTypes.object,
  errors: PropTypes.object,
  index: PropTypes.number,
  isValid: PropTypes.bool.isRequired,
  handleClick: PropTypes.func,
  customLabel: PropTypes.func,
  media: PropTypes.shape({
    tabletUp: PropTypes.bool
  }).isRequired
};

export default Field;



// WEBPACK FOOTER //
// ./src/client/js/view/components/UserForm/Field/Field.js